import { useEffect, useRef } from "react";
import {
  Copy,
  X,
  Share2,
  Download,
  Pencil,
  Trash2,
} from "lucide-react";
import { shareFile } from "../../lib/shareFile";
import { uploadUrl } from "../../lib/uploads";
import { TARGET_DEFS } from "../ConvertFromButton";

// [Iter 326 — 2026-02-24] Build the live authed URL for a chat message
// file at click-time. Prefer the bare `upload_id` (so the token is
// re-stamped with the CURRENT session) and only fall back to the
// stored `upload_url` for legacy rows.
function _liveUploadUrl(msg) {
  if (msg?.upload_id) return uploadUrl(msg.upload_id);
  return msg?.upload_url || "";
}

/**
 * MessageActionsMenu — Wave 19 #94.
 *
 * Right-click / long-press popover for a chat message. Lets the user
 * convert the message into a Task / Event / Job card / Ticket /
 * Procurement order — content is auto-extracted, customer is auto-linked,
 * and the new entity carries source_chat_thread_id + source_chat_message_id
 * for the "← from chat" breadcrumb pill.
 *
 * Labels & icons sourced from ConvertFromButton.TARGET_DEFS so they stay
 * in lockstep with every other "create from this" surface (Phase 8B).
 * Positioning stays bespoke (anchored to cursor x/y, clamped to viewport)
 * because right-click menus need that — it's not a generic
 * ConvertFromButton trigger.
 */

const TARGETS_ORDER = ["task", "event", "jobcard", "ticket", "procurement"];

export default function MessageActionsMenu({ x, y, onPick, onClose, onCopy, onEdit, onDelete, canEdit, canDelete, hasText, msg }) {
  const ref = useRef(null);
  const hasFile = !!(msg && ["image", "video", "file", "audio"].includes(msg.kind) && (msg.upload_id || msg.upload_url));

  // Dismiss on outside click / Escape
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Clamp to viewport
  const W = typeof window !== "undefined" ? window.innerWidth : 320;
  const H = typeof window !== "undefined" ? window.innerHeight : 600;
  const clampedX = Math.min(Math.max(8, x), W - 240);
  const clampedY = Math.min(Math.max(8, y), H - 320);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ left: clampedX, top: clampedY }}
      className="fixed z-[70] hv-card p-1 shadow-2xl min-w-[220px] animate-in fade-in zoom-in-95 duration-100"
      data-testid="chat-message-actions-menu"
    >
      <div className="flex items-center justify-between px-2 py-1 border-b border-white/5 mb-1">
        <span className="text-[9px] uppercase tracking-[0.2em] text-[#647087]">Convert message</span>
        <button
          type="button"
          onClick={onClose}
          className="text-[#647087] hover:text-white p-0.5 rounded"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {TARGETS_ORDER.map((target) => {
        const def = TARGET_DEFS[target];
        if (!def) return null;
        const { label, Icon, color } = def;
        return (
        <button
          key={target}
          type="button"
          onClick={() => {
            onPick?.(target);
          }}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition"
          data-testid={`chat-msg-convert-${target}`}
        >
          <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
          <span className="text-[#cbd2df]">{label}</span>
        </button>
        );
      })}
      {hasText && (
        <>
          <div className="border-t border-white/5 my-1" />
          <button
            type="button"
            onClick={onCopy}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition"
            data-testid="chat-msg-copy"
          >
            <Copy className="w-3.5 h-3.5 shrink-0 text-[#647087]" />
            <span className="text-[#cbd2df]">Copy text</span>
          </button>
        </>
      )}
      {hasFile && (
        <>
          <div className="border-t border-white/5 my-1" />
          <button
            type="button"
            onClick={async () => {
              onClose?.();
              await shareFile({
                url: _liveUploadUrl(msg),
                filename: msg.upload_filename,
                contentType: msg.upload_content_type,
                title: msg.upload_filename || "shared file",
              });
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition"
            data-testid="chat-msg-share-file"
          >
            <Share2 className="w-3.5 h-3.5 shrink-0 text-[#1FB6C1]" />
            <span className="text-[#cbd2df]">Share file…</span>
          </button>
          <button
            type="button"
            onClick={async () => {
              onClose?.();
              try {
                const resp = await fetch(_liveUploadUrl(msg), { credentials: "omit" });
                if (!resp.ok) throw new Error("HTTP " + resp.status);
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = msg.upload_filename || "download";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 30_000);
              } catch (err) {
                console.warn("download failed", err);
              }
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition"
            data-testid="chat-msg-download"
          >
            <Download className="w-3.5 h-3.5 shrink-0 text-emerald-300" />
            <span className="text-[#cbd2df]">Save to device</span>
          </button>
        </>
      )}
      {/* Edit / Delete — 2026-02-20 user request. Author edits their own
          text messages; author OR moderator can delete any of theirs. */}
      {(canEdit || canDelete) && (
        <>
          <div className="border-t border-white/5 my-1" />
          {canEdit && (
            <button
              type="button"
              onClick={() => { onEdit?.(); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-white/[0.05] transition"
              data-testid="chat-msg-edit"
            >
              <Pencil className="w-3.5 h-3.5 shrink-0 text-[#A78BFA]" />
              <span className="text-[#cbd2df]">Edit text</span>
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => { onDelete?.(); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-left rounded hover:bg-red-500/[0.10] transition"
              data-testid="chat-msg-delete"
            >
              <Trash2 className="w-3.5 h-3.5 shrink-0 text-red-400" />
              <span className="text-red-300">Delete message</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
